import { Fragment } from "react";
import { readOvertimeSplit } from "@/lib/offer-calc";
import {
  fmtPlain,
  fmtQty,
  fmtOvertimeBreakdown,
  exportLineDescription,
  type PhaseGroup,
} from "@/lib/offer-budget-image-geometry";
import {
  OFFER_EXPORT_FONT_DISPLAY_CSS,
  OFFER_EXPORT_HEX,
  OFFER_EXPORT_RULE_GAP_PX,
  OFFER_EXPORT_RULE_PX,
} from "@/lib/offer-export-brand";

/** Skillelinje mellom fasegruppene. Speiler TABLE_RULE i PPTX-en. */
const PHASE_RULE_CSS = `${OFFER_EXPORT_RULE_PX}px solid ${OFFER_EXPORT_HEX.tableBorder}`;

/**
 * Linjeboks for tabellrader. 1,35 klippet bunnen av g, j og y i
 * html2canvas (VIB-2557) — 1,55 gir underlengdene plass uten å spise
 * opp tettheten vi henter inn via lavere padding.
 */
const LINE_HEIGHT = 1.55;

interface PhaseTableProps {
  phases: PhaseGroup[];
  fontSize: number;
}

/**
 * VIB-702: rendrer ett sett phase-blokker som <table>. Trukket ut for å
 * støtte 2-kolonne-modus uten å duplisere tabell-koden. Bruker faste
 * kolonnebredder for tall/enheter så begge kolonnene ser like ut i
 * 2-kolonne-modus.
 */
export function PhaseTable({ phases, fontSize }: PhaseTableProps) {
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize,
        lineHeight: LINE_HEIGHT,
        tableLayout: "fixed",
      }}
    >
      {/*
        Malens kolonnefordeling (VIB-2556), som andel av tabellbredden:
        beskrivelse 50,6 %, mengde 12,4 %, enhet 17,1 %, «kr» 5,7 %,
        beløp 14,4 %. «kr» står i egen kolonne slik at beløpene flukter
        på venstre siffer — det er den synligste forskjellen mot malen.
      */}
      <colgroup>
        <col style={{ width: "50.6%" }} />
        <col style={{ width: "12.4%" }} />
        <col style={{ width: "17.1%" }} />
        <col style={{ width: "5.7%" }} />
        <col style={{ width: "14.4%" }} />
      </colgroup>
      <tbody>
        {(() => {
          const groups = phases.filter((p) => p.lines.length > 0);
          return groups.map(({ key, label, lines: phaseLines }, phaseIdx) => {
          // VIB-2556: malens radhøyde er 0,178" ≈ 17 px på vårt lerret,
          // mot våre tidligere ~21 px. Padding ned tilsvarende, mens
          // LINE_HEIGHT holder linjeboksen høy nok for g, j og y.
          const rowPad = Math.max(0, Math.round(fontSize * 0.05));
          // Malens fase-header er 0,376–0,411" mot linjenes 0,356", altså
          // bare litt høyere. Vår gamle 0,8/0,25-skalering gjorde den 35 %
          // høyere enn raden og spiste plass vi trenger til linjer.
          const phaseHeadTop = Math.max(2, Math.round(fontSize * 0.35));
          const phaseHeadBottom = Math.max(1, Math.round(fontSize * 0.1));
          return (
            <Fragment key={key}>
              <tr>
                <td
                  colSpan={5}
                  style={{
                    fontWeight: 600,
                    fontFamily: OFFER_EXPORT_FONT_DISPLAY_CSS,
                    paddingTop: phaseHeadTop,
                    paddingBottom: phaseHeadBottom,
                    lineHeight: LINE_HEIGHT,
                    verticalAlign: "middle",
                    // Skillelinje MELLOM fasene — den første gruppa får
                    // ingen, ellers åpner tabellen med en strek mot ingenting.
                    borderTop: phaseIdx > 0 ? PHASE_RULE_CSS : undefined,
                  }}
                >
                  {label}
                </td>
              </tr>
              {phaseLines.map((l, lineIdx) => {
                // Linja tegnes på radgrensen. Uten luft under siste rad i
                // gruppa lander den nede i underlengdene på g, j og y, og
                // går tvers gjennom teksten (Erik 29.09). Bare der det
                // FAKTISK kommer en linje etter — siste gruppe har ingen.
                const closesGroup =
                  lineIdx === phaseLines.length - 1 &&
                  phaseIdx < groups.length - 1;
                // Eksplisitt lineHeight og verticalAlign på hver td
                // — html2canvas arver ikke pålitelig fra <table>.
                // Uten dette klippes descender-bokstaver (g, j, p, y).
                const qty = l.quantity ?? 0;
                // VIB-663f: overtid-split rendres som sub-rad under linja.
                const split = readOvertimeSplit({
                  overtime_split: l.overtime_split ?? null,
                });
                const overtimeText = split
                  ? fmtOvertimeBreakdown(split, qty, l.unit ?? "t")
                  : null;
                // VIB-663b: eksterne linjer rendres i kursiv.
                const isExternal = l.cost_type === "external";
                const tdBase = {
                  paddingTop: rowPad,
                  paddingBottom: closesGroup
                    ? rowPad + OFFER_EXPORT_RULE_GAP_PX
                    : rowPad,
                  lineHeight: LINE_HEIGHT,
                  verticalAlign: "middle" as const,
                  fontStyle: isExternal
                    ? ("italic" as const)
                    : ("normal" as const),
                };
                return (
                  <tr key={l.id}>
                    <td
                      style={{
                        ...tdBase,
                        paddingRight: 8,
                        // VIB-2557: ingen overflow:hidden her, og ingen
                        // ellipsis. html2canvas klipper ALT innhold i et
                        // element med overflow:hidden ved tekstboksens høyde,
                        // uansett padding — det kuttet bunnen av hver eneste
                        // linje med g, j eller y. Et langt navn brekker nå
                        // over to linjer i stedet for å bli forkortet, som er
                        // det malen ville gjort.
                        overflow: "visible",
                        whiteSpace: "normal",
                      }}
                    >
                      {exportLineDescription(l.description)}
                      {overtimeText && (
                        <div
                          style={{
                            fontSize: Math.max(fontSize - 2, 7),
                            color: OFFER_EXPORT_HEX.tableMuted,
                            fontStyle: "italic",
                            lineHeight: 1.25,
                            whiteSpace: "normal",
                            overflow: "visible",
                            textOverflow: "clip",
                          }}
                        >
                          {overtimeText}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        ...tdBase,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {qty > 0 ? fmtQty(qty) : ""}
                    </td>
                    <td style={{ ...tdBase, textAlign: "left", paddingLeft: 6 }}>
                      {qty > 0 ? l.unit ?? "stk" : ""}
                    </td>
                    <td style={{ ...tdBase, textAlign: "left" }}>kr</td>
                    <td
                      style={{
                        ...tdBase,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {fmtPlain(l.amount ?? 0)}
                    </td>
                  </tr>
                );
              })}
              </Fragment>
            );
          });
        })()}
      </tbody>
    </table>
  );
}
